import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
  arrayUnion
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase';

/**
 * QnA 서비스
 * qnas 컬렉션 관리 (앱과 동일한 구조)
 * 구조: qnas/{qnaId} with messages array
 */
export const qnaService = {
  /**
   * 특정 부모(학생)의 모든 QnA 목록 가져오기
   */
  async getStudentQnAs(parentId) {
    try {
      const qnaRef = collection(db, 'qnas');
      const q = query(qnaRef, where('parentId', '==', parentId));
      const snapshot = await getDocs(q);

      const results = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Timestamp 객체를 Date로 변환
          createdAt: data.createdAt?.toDate?.() || data.createdAt,
          updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
          messages: (data.messages || []).map(msg => ({
            ...msg,
            createdAt: msg.createdAt?.toDate?.() || msg.createdAt
          }))
        };
      });

      // updatedAt 기준으로 정렬 (최신순)
      results.sort((a, b) => {
        const aTime = a.updatedAt?.getTime?.() || 0;
        const bTime = b.updatedAt?.getTime?.() || 0;
        return bTime - aTime;
      });

      return results;
    } catch (error) {
      console.error('Error fetching student QnAs:', error);
      throw error;
    }
  },

  /**
   * 특정 프로그램의 QnA 목록 가져오기
   * 앱에서는 프로그램ID를 따로 저장하지 않으므로 childId로 필터링
   */
  async getProgramQnAs(parentId, childId) {
    try {
      const qnaRef = collection(db, 'qnas');
      const q = query(
        qnaRef,
        where('parentId', '==', parentId),
        where('childId', '==', childId)
      );
      const snapshot = await getDocs(q);

      const results = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate?.() || data.createdAt,
          updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
          messages: (data.messages || []).map(msg => ({
            ...msg,
            createdAt: msg.createdAt?.toDate?.() || msg.createdAt
          }))
        };
      });

      // updatedAt 기준으로 정렬 (최신순)
      results.sort((a, b) => {
        const aTime = a.updatedAt?.getTime?.() || 0;
        const bTime = b.updatedAt?.getTime?.() || 0;
        return bTime - aTime;
      });

      return results;
    } catch (error) {
      console.error('Error fetching program QnAs:', error);
      throw error;
    }
  },

  /**
   * QnA 질문 생성 (학부모용) - 웹에서는 사용 안 함 (앱에서만 생성)
   */
  async createQuestion(parentId, parentName, childId, childName, questionData, images = []) {
    try {
      // 새 문서 참조 생성
      const qnaRef = collection(db, 'qnas');
      const docRef = doc(qnaRef);
      const qnaId = docRef.id;

      // 이미지 업로드
      const imageUrls = await this.uploadImages(qnaId, images);

      // 초기 메시지 생성
      const initialMessage = {
        id: this._generateId(),
        userId: parentId,
        userName: parentName,
        userType: 'parent',
        content: questionData.content,
        imageUrls: imageUrls,
        fileUrls: [],
        createdAt: new Date().toISOString()
      };

      // QnA 문서 생성
      await addDoc(qnaRef, {
        parentId,
        parentName,
        childId: childId || '',
        childName: childName || '',
        title: questionData.title,
        status: 'open',
        messages: [initialMessage],
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });

      return qnaId;
    } catch (error) {
      console.error('Error creating question:', error);
      throw error;
    }
  },

  /**
   * QnA 답변 작성 (치료사/관리자용) - 메시지 추가 방식
   */
  async answerQuestion(qnaId, answerData, images = []) {
    try {
      // 이미지 업로드
      const imageUrls = await this.uploadImages(qnaId, images);

      // 새 답변 메시지 생성
      const answerMessage = {
        id: this._generateId(),
        userId: answerData.userId,
        userName: answerData.userName,
        userType: 'instructor',
        content: answerData.content,
        imageUrls: imageUrls,
        fileUrls: [],
        createdAt: new Date().toISOString()
      };

      // QnA 문서 업데이트
      const qnaRef = doc(db, 'qnas', qnaId);
      await updateDoc(qnaRef, {
        messages: arrayUnion(answerMessage),
        status: 'answered',
        updatedAt: Timestamp.now()
      });

      return true;
    } catch (error) {
      console.error('Error answering question:', error);
      throw error;
    }
  },

  /**
   * 이미지 파일들을 Firebase Storage에 업로드
   */
  async uploadImages(qnaId, images) {
    try {
      const uploadPromises = images.map(async (image, index) => {
        const timestamp = Date.now();
        const filename = `qna/${qnaId}/image_${index}_${timestamp}_${image.name}`;
        const storageRef = ref(storage, filename);

        await uploadBytes(storageRef, image);
        const url = await getDownloadURL(storageRef);
        return url;
      });

      return await Promise.all(uploadPromises);
    } catch (error) {
      console.error('Error uploading images:', error);
      throw error;
    }
  },

  /**
   * QnA 삭제 (관리자용)
   */
  async deleteQnA(qnaId) {
    try {
      const qnaRef = doc(db, 'qnas', qnaId);
      await deleteDoc(qnaRef);
      return true;
    } catch (error) {
      console.error('Error deleting QnA:', error);
      throw error;
    }
  },

  /**
   * 미답변 QnA 개수 가져오기
   */
  async getUnansweredCount(parentId) {
    try {
      const qnaList = await this.getStudentQnAs(parentId);
      return qnaList.filter(qna => qna.status === 'open').length;
    } catch (error) {
      console.error('Error getting unanswered count:', error);
      return 0;
    }
  },

  /**
   * 랜덤 ID 생성 (앱과 동일한 방식)
   */
  _generateId() {
    return Math.random().toString(36).substr(2, 9) +
           Math.random().toString(36).substr(2, 9);
  }
};
